import { createBrowserClient } from '@supabase/ssr';

// =====================================================================
// SSO via cookies partagés sur .recruteurs.io
//
// Avant : `createClient` de @supabase/supabase-js avec storage=localStorage.
//         La session restait sur le domaine courant uniquement.
//
// Maintenant : `createBrowserClient` de @supabase/ssr écrit la session
// dans des cookies. En prod, on force `domain=.recruteurs.io` pour que
// la session soit lisible à la fois par https://recruteurs.io (ce site)
// et par https://app.recruteurs.io (ATS Next.js). Un seul login partout.
//
// Migration : à la première visite après déploiement, les sessions en
// localStorage seront ignorées (clé/format différents). Les utilisateurs
// devront se reconnecter une fois.
// =====================================================================

// Mock data for offline development
const mockArticles = [
  {
    id: '1',
    title: 'Comment optimiser votre processus de recrutement en 2024',
    excerpt: 'Découvrez les meilleures pratiques pour améliorer votre stratégie de recrutement et attirer les meilleurs talents.',
    content: 'Le recrutement moderne nécessite une approche stratégique...',
    image_url: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg',
    author: 'Équipe TalentAI',
    category: 'Recrutement',
    slug: 'optimiser-processus-recrutement-2024',
    created_at: '2024-01-15T10:30:00.000Z',
    published: true
  },
  {
    id: '2',
    title: "L'intelligence artificielle au service des RH",
    excerpt: "Comment l'IA transforme les ressources humaines et améliore l'expérience candidat.",
    content: "L'intelligence artificielle révolutionne le secteur des RH...",
    image_url: 'https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg',
    author: 'Équipe TalentAI',
    category: 'Innovation RH',
    slug: 'ia-service-rh',
    created_at: '2024-01-14T10:30:00.000Z',
    published: true
  },
  {
    id: '3',
    title: 'Construire une marque employeur attractive',
    excerpt: 'Les clés pour développer une marque employeur qui attire et fidélise les talents.',
    content: 'Une marque employeur forte est essentielle...',
    image_url: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg',
    author: 'Équipe TalentAI',
    category: 'Marque employeur',
    slug: 'construire-marque-employeur',
    created_at: '2024-01-13T10:30:00.000Z',
    published: true
  }
];

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Cookie domain : `.recruteurs.io` en prod, non défini en dev (localhost
// partage déjà les cookies entre ports). Configurable via env.
const cookieDomain =
  import.meta.env.VITE_AUTH_COOKIE_DOMAIN ||
  (import.meta.env.PROD ? '.recruteurs.io' : undefined);

// Flag to track if we're in offline mode
let isOfflineMode = false;

// Clear any corrupted auth tokens on app startup (legacy localStorage)
if (typeof window !== 'undefined') {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.includes('sb-') && key.includes('-auth-token')) {
        try {
          const value = localStorage.getItem(key);
          if (value) {
            const parsed = JSON.parse(value);
            if (parsed?.access_token) {
              try {
                const parts = parsed.access_token.split('.');
                if (parts.length === 3) {
                  const payload = JSON.parse(atob(parts[1]));
                  if (!payload.sub || !payload.exp) {
                    console.warn(`🔒 Found corrupted JWT in ${key}, clearing...`);
                    localStorage.removeItem(key);
                  }
                } else {
                  console.warn(`🔒 Invalid JWT format in ${key}, clearing...`);
                  localStorage.removeItem(key);
                }
              } catch {
                console.warn(`🔒 Could not decode JWT in ${key}, clearing...`);
                localStorage.removeItem(key);
              }
            }
          }
        } catch (e) {
          console.warn(`🔒 Could not parse auth token in ${key}, clearing...`);
          localStorage.removeItem(key);
        }
      }
    });
  } catch (error) {
    console.error('Error checking auth tokens:', error);
  }
}

// Enhanced validation of environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  const missingVars = [];
  if (!supabaseUrl) missingVars.push('VITE_SUPABASE_URL');
  if (!supabaseAnonKey) missingVars.push('VITE_SUPABASE_ANON_KEY');

  const errorMessage = `Missing required Supabase environment variables: ${missingVars.join(', ')}. Please ensure you have connected your Supabase project by clicking the "Connect to Supabase" button in the top right corner.`;
  console.error('❌ Supabase configuration error:', errorMessage);
  console.warn('🔄 Switching to offline mode with mock data');
  isOfflineMode = true;
}

// Create supabase client only if we have valid config
let supabase: any = null;

if (!isOfflineMode) {
  // Validate URL format
  try {
    new URL(supabaseUrl);
  } catch (error) {
    const errorMessage = `Invalid Supabase URL format: ${supabaseUrl}. Please ensure you have connected your Supabase project correctly.`;
    console.error('❌ Supabase configuration error:', errorMessage);
    console.warn('🔄 Switching to offline mode with mock data');
    isOfflineMode = true;
  }

  if (!isOfflineMode && (!supabaseAnonKey.startsWith('eyJ') || supabaseAnonKey.length < 100)) {
    const errorMessage = 'Invalid Supabase anon key format. Please ensure you have connected your Supabase project correctly.';
    console.error('❌ Supabase configuration error:', errorMessage);
    console.warn('🔄 Switching to offline mode with mock data');
    isOfflineMode = true;
  }
}

if (!isOfflineMode) {
  supabase = createBrowserClient(supabaseUrl, supabaseAnonKey, {
    cookieOptions: {
      domain: cookieDomain,
      path: '/',
      sameSite: 'lax',
      secure: import.meta.env.PROD,
    },
    auth: {
      flowType: 'pkce',
      // @supabase/ssr enables detectSessionInUrl by default — keep the
      // legacy explicit-false behavior. Pages that need it (LinkedIn
      // OAuth landing) can call exchangeCodeForSession manually.
      detectSessionInUrl: false,
    },
    global: {
      headers: {
        'X-Client-Info': 'supabase-ssr-web'
      }
    },
    ...(import.meta.env.DEV && {
      db: {
        schema: 'public'
      },
      realtime: {
        params: {
          eventsPerSecond: 10
        }
      }
    })
  });
} else {
  // Create a mock supabase object for offline mode
  supabase = {
    from: (table: string) => ({
      select: (columns?: string, options?: any) => ({
        eq: (column: string, value: any) => ({
          order: (column: string, options?: any) => ({
            limit: (count: number) => Promise.resolve({
              data: table === 'articles' ? mockArticles.slice(0, count) : [],
              error: null
            })
          }),
          single: () => Promise.resolve({
            data: table === 'articles' ? mockArticles[0] : null,
            error: null
          })
        }),
        order: (column: string, options?: any) => ({
          limit: (count: number) => Promise.resolve({
            data: table === 'articles' ? mockArticles.slice(0, count) : [],
            error: null
          })
        }),
        limit: (count: number) => Promise.resolve({
          data: table === 'articles' ? mockArticles.slice(0, count) : [],
          error: null
        })
      }),
      insert: (data: any) => Promise.resolve({ data: null, error: null }),
      update: (data: any) => Promise.resolve({ data: null, error: null }),
      delete: () => Promise.resolve({ data: null, error: null })
    }),
    auth: {
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
      signUp: () => Promise.resolve({ data: null, error: null }),
      signInWithPassword: () => Promise.resolve({ data: null, error: null }),
      signOut: () => Promise.resolve({ error: null }),
      refreshSession: () => Promise.resolve({ data: { session: null }, error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } })
    }
  };
}

export { supabase };

// Helper function to check if error is CORS-related
const isCorsError = (error: any): boolean => {
  return (
    error?.message?.includes('Failed to fetch') ||
    error?.message?.includes('CORS') ||
    error?.message?.includes('Network request failed') ||
    error?.code === 'PGRST301' ||
    (error?.status === 0 && !error?.statusText)
  );
};

// Helper function to check if error is network-related
const isNetworkError = (error: any): boolean => {
  return (
    isCorsError(error) ||
    error?.message?.includes('NetworkError') ||
    error?.message?.includes('ERR_NETWORK') ||
    error?.message?.includes('ERR_INTERNET_DISCONNECTED')
  );
};

// Enhanced connection test with detailed error reporting and retry logic
export const testConnection = async () => {
  if (isOfflineMode) {
    console.log('🔄 Running in offline mode with mock data');
    return true;
  }

  const maxRetries = 3;
  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      const { error: authError } = await supabase.auth.getUser();
      if (authError?.status === 403 && authError.message?.includes('missing sub claim')) {
        console.warn('❌ Detected invalid JWT, clearing all auth tokens...');
        await supabase.auth.signOut();
        Object.keys(localStorage).forEach(key => {
          if (key.includes('sb-') && key.includes('-auth-token')) {
            localStorage.removeItem(key);
          }
        });
      }

      const { error } = await supabase
        .from('articles')
        .select('*', { count: 'exact', head: true })
        .eq('published', true);

      if (error) {
        if (isCorsError(error)) {
          console.warn('❌ Supabase connection error (Failed to fetch). Check network or Supabase project status.');
          console.warn('Current URL:', window.location.origin);
          return false;
        } else if (error.code === '401') {
          console.error('❌ Supabase authentication error: Invalid API key');
        } else if (error.status === 403 && error.message?.includes('missing sub claim')) {
          console.warn('❌ Invalid JWT detected, clearing all auth tokens...');
          await supabase.auth.signOut();
          Object.keys(localStorage).forEach(key => {
            if (key.includes('sb-') && key.includes('-auth-token')) {
              localStorage.removeItem(key);
            }
          });
          retryCount++;
          if (retryCount < maxRetries) {
            console.log(`Retrying connection (attempt ${retryCount + 1}/${maxRetries})...`);
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            continue;
          }
        } else {
          console.error('❌ Supabase query error:', error.message);
        }

        retryCount++;
        if (retryCount < maxRetries && !isCorsError(error)) {
          console.log(`Retrying connection (attempt ${retryCount + 1}/${maxRetries})...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          continue;
        }
        return false;
      }

      console.log('✅ Supabase connection successful - Online mode');
      return true;
    } catch (error) {
      if (isCorsError(error)) {
        console.warn('❌ Supabase connection error (Failed to fetch). Check network or Supabase project status.');
        console.warn('Current URL:', window.location.origin);
        return false;
      }

      console.error('❌ Supabase connection error:', error);
      retryCount++;
      if (retryCount < maxRetries) {
        console.log(`Retrying connection (attempt ${retryCount + 1}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        continue;
      }
      return false;
    }
  }

  return false;
};

// Get current session safely with validation and retry logic
export const getCurrentSession = async () => {
  if (isOfflineMode) {
    return null;
  }

  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      if (isCorsError(error)) {
        return null;
      }
      if (error.status === 403 && error.message?.includes('missing sub claim')) {
        await supabase.auth.signOut();
        return null;
      }
      return null;
    }

    if (!session?.user?.id || !session?.access_token) {
      return null;
    }

    return session;
  } catch (error) {
    return null;
  }
};

// Get current user safely with validation
export const getCurrentUser = async () => {
  if (isOfflineMode) {
    return null;
  }

  try {
    const session = await getCurrentSession();
    if (!session?.user?.id) {
      return null;
    }

    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) {
      if (isCorsError(error)) {
        console.warn('❌ CORS error getting user, returning null');
        return null;
      }
      if (error.status === 403 && error.message?.includes('missing sub claim')) {
        console.warn('❌ Invalid JWT detected in user fetch, clearing all auth tokens...');
        await supabase.auth.signOut();
        Object.keys(localStorage).forEach(key => {
          if (key.includes('sb-') && key.includes('-auth-token')) {
            localStorage.removeItem(key);
          }
        });
        return null;
      }
      console.error('User fetch error:', error);
      return null;
    }

    if (!user?.id) {
      return null;
    }

    return user;
  } catch (error) {
    if (isCorsError(error)) {
      console.warn('❌ CORS error getting user, returning null');
      return null;
    }
    console.error('Error getting user:', error);
    return null;
  }
};

// Refresh session with enhanced validation
export const refreshSession = async () => {
  if (isOfflineMode) {
    return null;
  }

  try {
    const currentSession = await getCurrentSession();
    if (!currentSession) {
      return null;
    }

    const { data: { session }, error } = await supabase.auth.refreshSession();

    if (error) {
      if (isCorsError(error)) {
        console.warn('❌ CORS error refreshing session, returning null');
        return null;
      }
      if (error.status === 403 && error.message?.includes('missing sub claim')) {
        console.warn('❌ Invalid JWT detected during refresh, clearing all auth tokens...');
        await supabase.auth.signOut();
        Object.keys(localStorage).forEach(key => {
          if (key.includes('sb-') && key.includes('-auth-token')) {
            localStorage.removeItem(key);
          }
        });
        return null;
      }
      console.error('Session refresh error:', error);
      return null;
    }

    if (!session?.user?.id || !session?.access_token) {
      return null;
    }

    return session;
  } catch (error) {
    if (isCorsError(error)) {
      console.warn('❌ CORS error refreshing session, returning null');
      return null;
    }
    console.error('Error refreshing session:', error);
    return null;
  }
};

// Safe Supabase query wrapper
export const safeSupabaseQuery = async <T>(
  queryFn: () => Promise<{ data: T; error: any }>,
  fallbackValue: T | null = null
): Promise<{ data: T | null; error: any; isCorsError: boolean }> => {
  if (isOfflineMode) {
    if (queryFn.toString().includes('articles')) {
      return {
        data: mockArticles as any,
        error: null,
        isCorsError: false
      };
    }
    return {
      data: fallbackValue,
      error: null,
      isCorsError: false
    };
  }

  try {
    const result = await queryFn();

    if (result.error) {
      const corsError = isCorsError(result.error);
      if (corsError) {
        console.warn('❌ Supabase connection error in query (Failed to fetch). Check network.');
      }
      return {
        data: fallbackValue,
        error: result.error,
        isCorsError: corsError
      };
    }

    return {
      data: result.data,
      error: null,
      isCorsError: false
    };
  } catch (error) {
    const corsError = isCorsError(error);
    if (corsError) {
      console.warn('❌ Supabase connection error in query (Failed to fetch). Check network.');
    }
    return {
      data: fallbackValue,
      error,
      isCorsError: corsError
    };
  }
};

export default supabase;
